import { DateTime } from 'luxon';
import { GCGroup } from "../redux/Reducers/gcGroupReducer";
import { CreatePostType } from "../util/CreatePost";
import { isRegularAppUser } from "../util/utils";
import appServices from "./app-services/appServices";
import { prefectures } from "../mng/constants/statistics";
import ja from '../common/constants/ja';
import { isAdmin, isNavigator } from '../mng/util/util';

const gtmId = process.env.REACT_APP_GTM_ID && `${process.env.REACT_APP_GTM_ID}`.length > 0 ? `${process.env.REACT_APP_GTM_ID}` : undefined;

export const initGTM = () => {
  if (gtmId) {
    let script1 = document.createElement('script');
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=' + gtmId;
    script1.async = true;
    document.head.appendChild(script1);

    let script2 = document.createElement('script');
    script2.text = `window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('consent', 'default', {
        // Cookie を利用しないと探索ビューを利用できない
        ad_storage: 'granted',
        analytics_storage: 'granted',
        functionality_storage: 'granted',
        personalization_storage: 'granted'
      });
      gtag('js', new Date());
      gtag('config', '${gtmId}');`;
    document.head.appendChild(script2);
  }
}

export const pushPageView = (user: any, params: any) => {
  if (!gtmId) {
    return
  }
  if (!user || (typeof user === 'object' && Object.keys(user).length === 0)) {
    // ログイン前のページ遷移を除く
    return
  }
  if (isAdmin(user) || isNavigator(user)) {
    return
  }
  new Promise(() => {
    pushEvent('page_view', params);
  });
}

export const pushLogin = (user: any) => {
  if (!gtmId) {
    return
  }

  new Promise(() => {
    window.gtag('config', `${process.env.REACT_APP_GTM_ID}`, {
      'user_id': user.external_id,
      // 広告パーソナライズを無効化
      allow_ad_personalization_signals: false
    });

    if (isRegularAppUser(user)) {
      pushEvent('login', {
        'source': 'user',
        'user_id': user.external_id,
        'external_id': user.external_id,
        'prefecture': getPrefecture(user),
        'generation': getGeneration(user)
      });
      const params = {status: 'approved'}
      appServices.getMemberGroups(params).then((res: any) => {
        res.member_groups
          .filter((member_group: any) => member_group.status == null || member_group.status === 'approved') // リーダー or それ以外
          .forEach((member_group: any) => {
            pushEvent('login', {
              'source': 'member_group',
              'member_group_id': member_group.id,
              'member_group_name': member_group.name ?? '（未設定）',
              'user_id': user.external_id,
              'external_id': user.external_id
            });
          });
      }).catch(() => {
        // pass
      });
    } else {
      pushEvent('login', {
        'source': 'user',
        'user_id': user.external_id,
        'external_id': user.external_id,
        'prefecture': getPrefecture(user),
        'generation': getGeneration(user)
      });
    }
  });
}

export const pushUserLike = (user: any) => {
  if (!gtmId) {
    return
  }

  new Promise(() => {
    pushEvent('add_like', {
      'source': 'user',
      'label': 'いいね',
      'user_id': user.external_id,
      'external_id': user.external_id,
      'prefecture': getPrefecture(user),
      'generation': getGeneration(user)
    });
  });
}

export const pushUserComment = (user: any, post: any) => {
  if (!gtmId) {
    return
  }

  new Promise(() => {
    pushEvent('create_comment', {
      'source': 'user',
      'label': 'コメント',
      'user_id': user.external_id,
      'external_id': user.external_id,
      'post_type': getPostType(post.is_private ? 'PrivateUserPost' : 'PublicUserPost'),
      'prefecture': getPrefecture(user),
      'generation': getGeneration(user)
    });
  });
}

export const pushMemberGroupComment = (user: any, post: any) => {
  if (!gtmId) {
    return
  }

  new Promise(() => {
    let postType = getPostType('GroupPublicPost');
    if (post.post_events && post.post_events.length > 0) {
      postType = getPostType('GroupEventPost');
    } else if (post.is_private_group_user) {
      postType = getPostType('GroupPrivateUserPost');
    } else if (post.is_private) {
      postType = getPostType('InsideGroupPost');
    }
    pushEvent('create_comment', {
      'source': 'member_group',
      'label': 'コメント',
      'member_group_id': post.member_group.id,
      'member_group_name': post.member_group.name ?? '（未設定）',
      'user_id': user.external_id,
      'external_id': user.external_id,
      'post_type': postType
    });
  });
}

export const pushUserPost = (user: any, isPrivate: boolean) => {
  if (!gtmId) {
    return
  }

  new Promise(() => {
    pushEvent('create_post', {
      'source': 'user',
      'label': '投稿',
      'user_id': user.external_id,
      'external_id': user.external_id,
      'post_type': getPostType(isPrivate ? 'PrivateUserPost' : 'PublicUserPost'),
      'prefecture': getPrefecture(user),
      'generation': getGeneration(user)
    });
  });
}

export const pushUserPostTag = (user: any, isPrivate: boolean, tagAttributes: any) => {
  if (!gtmId) {
    return
  }

  new Promise(() => {
    const tags = tagAttributes?.filter((attr: any) => attr.id == null).map((attr:any) => attr.tag)
    tags?.forEach((tag: string) => {
      pushEvent('add_tag', {
        'source': 'user',
        'user_id': user.external_id,
        'external_id': user.external_id,
        'post_type': getPostType(isPrivate ? 'PrivateUserPost' : 'PublicUserPost'),
        'prefecture': getPrefecture(user),
        'generation': getGeneration(user),
        'tag': tag
      });
    })
  });
}

export const pushMemberGroupPost = (user: any, groupInfo: GCGroup, createPostType: CreatePostType, tagAttributes: any) => {
  if (!gtmId) {
    return
  }

  new Promise(() => {
    pushEvent('create_post', {
      'source': 'member_group',
      'label': '投稿',
      'member_group_id': groupInfo.id,
      'member_group_name': groupInfo.groupName ?? '（未設定）',
      'user_id': user.external_id,
      'external_id': user.external_id,
      'post_type': getPostType(createPostType)
    });
  });
}

// カスタムイベント名も送信できるよう Gtag.EventNames は指定しない
const pushEvent = (eventName: string, data: Record<string, unknown>) => {
  window.gtag('event', eventName, data);
}

const getGeneration = (user: any): string | undefined => {
  if (!user.dob) {
    return undefined;
  }
  const birthDate = DateTime.fromISO(user.dob, { zone: 'Asia/Tokyo' });
  const now = DateTime.now().setZone('Asia/Tokyo');

  let age = now.year - birthDate.year;
  if (now < birthDate.plus({ years: age })) {
    // 誕生日がまだ来ていない
    age--;
  }

  const generation = [
      { generation: '-9', min: 0, max: 9, name: ja.mng_users_utilization_generation__9 },
      { generation: '10-17', min: 10, max: 17, name: ja.mng_users_utilization_generation_10_17 },
      { generation: '18-19', min: 18, max: 19, name: ja.mng_users_utilization_generation_18_19 },
      { generation: '20-29', min: 20, max: 29, name: ja.mng_users_utilization_generation_20_29 },
      { generation: '30-39', min: 30, max: 39, name: ja.mng_users_utilization_generation_30_39 },
      { generation: '40-49', min: 40, max: 49, name: ja.mng_users_utilization_generation_40_49 },
      { generation: '50-59', min: 50, max: 59, name: ja.mng_users_utilization_generation_50_59 },
      { generation: '60-69', min: 60, max: 69, name: ja.mng_users_utilization_generation_60_69 },
      { generation: '70-79', min: 70, max: 79, name: ja.mng_users_utilization_generation_70_79 },
      { generation: '80-', min: 80, max: undefined, name: ja.mng_users_utilization_generation_80_ }
  ].find((item: any) => {
    if (item.min !== undefined && item.max !== undefined) {
      return item.min <= age && age <= item.max
    } else if (item.max === undefined) {
      return item.min <= age
    } else {
      return undefined
    }
  });
  return generation ? generation.name : '(不明)';
}

const getPrefecture = (user: any): string => {
  const prefecture = user.address ? prefectures.find((str: string) => user.address?.match(`^${str}`)) : undefined;
  return prefecture ?? '（不明）'
}

const getPostType = (postType: CreatePostType | 'PublicUserPost' | 'PrivateUserPost'): string => {
  switch(postType) {
    case 'PublicUserPost': {
      return 'オープン投稿'
    }
    case 'PrivateUserPost': {
      return 'プライベート投稿'
    }
    case 'GroupPublicPost': {
      return 'みんなの投稿'
    }
    case 'InsideGroupPost': {
      return 'グループ内の投稿'
    }
    case 'GroupPrivateUserPost': {
      return 'グループ内のプライベート投稿'
    }
    case 'GroupEventPost': {
      return 'イベント'
    }
    default: {
      return '（不明）'
    }
  }
}
